import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plug, Plus, Trash2, Loader2, RotateCcw, Copy, Check, ChevronDown, ChevronRight, Eye, EyeOff, Clock, AlertCircle, Zap, Database, CreditCard, Globe, Code, Plane } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { IndustryComboBox } from '@/components/IndustryComboBox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  database: Database,
  'credit-card': CreditCard,
  globe: Globe,
  code: Code,
  plane: Plane,
  receipt: CreditCard,
};

const SourceTypeTag = ({ sourceType, sourceTypes }) => {
  const st = sourceTypes.find(s => s.id === sourceType);
  const label = st?.label || sourceType;
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700" data-testid="connector-source-tag">
      {label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    disabled: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${styles[status] || styles.disabled}`} data-testid="connector-status-badge">
      {status}
    </span>
  );
};

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(`${label || 'Text'} copied`);
  };
  return (
    <button onClick={handleCopy} className="p-1 text-slate-400 hover:text-slate-700 transition-colors" data-testid={`copy-${(label || 'text').toLowerCase().replace(/\s/g, '-')}-btn`}>
      {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
    </button>
  );
};

const DEFAULT_FIELD_MAP = {
  supplier: 'supplier',
  description: 'description',
  amount: 'amount',
  date: 'date',
  currency: 'currency',
};

const ConnectorForm = ({ sourceTypes, token, onCreated, onCancel }) => {
  const [name, setName] = useState('');
  const [sourceType, setSourceType] = useState('webhook');
  const [industry, setIndustry] = useState('');
  const [autoProcess, setAutoProcess] = useState(true);
  const [fieldMapping, setFieldMapping] = useState({ ...DEFAULT_FIELD_MAP });
  const [showMapping, setShowMapping] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Connector name is required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/connectors`, {
        name: name.trim(),
        source_type: sourceType,
        industry,
        auto_process: autoProcess,
        field_mapping: fieldMapping,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create connector');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5" data-testid="connector-form">
      <h3 className="text-sm font-semibold text-slate-900">New Connector</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. SAP Production Feed"
            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="connector-name-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Source Type</label>
          <select value={sourceType} onChange={e => setSourceType(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="connector-source-select">
            {sourceTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Industry Context</label>
        <IndustryComboBox token={token} value={industry} onChange={setIndustry} />
        <p className="text-xs text-slate-400 mt-1">Used by AI to improve classification accuracy for ingested data</p>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={() => setAutoProcess(!autoProcess)}
          className={`relative w-10 h-5 rounded-full transition-colors ${autoProcess ? 'bg-emerald-500' : 'bg-slate-300'}`}
          data-testid="connector-auto-process-toggle">
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoProcess ? 'left-5' : 'left-0.5'}`} />
        </button>
        <div>
          <span className="text-sm font-medium text-slate-700">Auto-process on ingestion</span>
          <p className="text-xs text-slate-400">Automatically cleanse and classify incoming data</p>
        </div>
      </div>

      <div>
        <button onClick={() => setShowMapping(!showMapping)}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
          data-testid="toggle-field-mapping">
          {showMapping ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Field Mapping (Advanced)
        </button>
        {showMapping && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 bg-slate-50 border border-slate-200 rounded-sm">
            {Object.entries(fieldMapping).map(([target, source]) => (
              <div key={target}>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{target}</label>
                <input value={source} onChange={e => setFieldMapping(prev => ({ ...prev, [target]: e.target.value }))}
                  placeholder={target}
                  className="w-full px-2.5 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid={`field-map-${target}`} />
              </div>
            ))}
            <p className="col-span-full text-xs text-slate-400 mt-1">Map your source system field names to DataRefinery fields</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="connector-cancel-btn">Cancel</Button>
        <Button size="sm" onClick={handleSave} disabled={saving}
          className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1" data-testid="connector-save-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Create Connector
        </Button>
      </div>
    </div>
  );
};

const ApiKeyReveal = ({ apiKey, connectorName }) => {
  const endpoint = `${BACKEND_URL}/api/ingest/{connector_id}`;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-sm p-5 space-y-4" data-testid="api-key-reveal">
      <div className="flex items-start gap-3">
        <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-semibold text-amber-800">Save your API Key — it won't be shown again</h4>
          <p className="text-xs text-amber-700 mt-1">Connector "<span className="font-medium">{connectorName}</span>" created successfully.</p>
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1.5">API Key</label>
        <div className="flex items-center gap-2 bg-white border border-amber-300 rounded-sm px-3 py-2">
          <code className="text-sm font-mono text-slate-900 flex-1 select-all" data-testid="api-key-value">{apiKey}</code>
          <CopyButton text={apiKey} label="API Key" />
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1.5">Ingestion Endpoint</label>
        <div className="flex items-center gap-2 bg-white border border-amber-300 rounded-sm px-3 py-2">
          <code className="text-xs font-mono text-slate-700 flex-1">POST {endpoint}</code>
          <CopyButton text={`POST ${endpoint}`} label="Endpoint" />
        </div>
      </div>

      <div className="bg-white border border-amber-200 rounded-sm p-3">
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Example cURL</p>
        <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="curl-example">{`curl -X POST "${BACKEND_URL}/api/ingest/{connector_id}" \\
  -H "X-API-Key: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"records": [
    {"supplier": "Acme Corp", "description": "Office Supplies", "amount": 1500.00, "date": "2025-01-15", "currency": "USD"}
  ]}'`}</pre>
        <CopyButton text={`curl -X POST "${BACKEND_URL}/api/ingest/{connector_id}" -H "X-API-Key: ${apiKey}" -H "Content-Type: application/json" -d '{"records": [{"supplier": "Acme Corp", "description": "Office Supplies", "amount": 1500.00}]}'`} label="cURL" />
      </div>
    </div>
  );
};

const IngestionHistory = ({ connectorId, token }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/connectors/${connectorId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.history || []);
    } catch { setHistory([]); }
    finally { setLoading(false); }
  }, [connectorId, token]);

  useEffect(() => { if (open) fetchHistory(); }, [open, fetchHistory]);

  return (
    <div className="mt-2">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
        data-testid={`history-toggle-${connectorId}`}>
        <Clock size={12} />
        {open ? 'Hide' : 'Show'} Ingestion History
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && (
        <div className="mt-2 border border-slate-100 rounded-sm overflow-hidden" data-testid={`history-panel-${connectorId}`}>
          {loading ? (
            <div className="flex items-center justify-center py-4"><Loader2 size={16} className="animate-spin text-slate-400" /></div>
          ) : history.length === 0 ? (
            <p className="text-xs text-slate-400 p-3">No ingestion events yet</p>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                <th className="text-left px-3 py-2">Timestamp</th>
                <th className="text-right px-3 py-2">Received</th>
                <th className="text-right px-3 py-2">Ingested</th>
                <th className="text-right px-3 py-2">Errors</th>
                <th className="text-center px-3 py-2">Status</th>
              </tr></thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/50">
                    <td className="px-3 py-2 font-mono text-slate-600">{new Date(h.timestamp).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{h.records_received}</td>
                    <td className="px-3 py-2 text-right text-slate-700 font-medium">{h.records_ingested}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{h.errors?.length || 0}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${h.status === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                        {h.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const QuickBooksCard = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/qbo/status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data);
    } catch (e) { /* endpoint may be 503 before creds are set */ }
  };
  useEffect(() => { load(); }, [token]);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/qbo/connect`, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start QuickBooks connection');
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/qbo/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Pulled ${res.data.pulled} records from QuickBooks (${res.data.bills} bills, ${res.data.purchases} purchases)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect QuickBooks? You can reconnect any time.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/qbo/disconnect`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('QuickBooks disconnected');
      await load();
    } catch (e) {
      toast.error('Failed to disconnect');
    } finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="quickbooks-card">
      <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-md bg-green-600 text-white flex items-center justify-center font-heading font-bold text-lg flex-shrink-0">QB</div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-slate-900">
                QuickBooks Online
                {status.connected && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Connected</span>}
                {status.enabled && !status.connected && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Available</span>}
                {!status.enabled && <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">Coming soon</span>}
              </h2>
              <p className="text-sm text-slate-600 mt-0.5">
                {status.enabled
                  ? `One-click OAuth connection to pull Bills and Purchases into DataRefinery. Environment: ${status.environment}.`
                  : 'Native QuickBooks OAuth is scaffolded and awaiting Intuit developer credentials in the backend env (QBO_CLIENT_ID + QBO_CLIENT_SECRET).'}
              </p>
              {status.connected && (
                <p className="text-xs text-slate-500 mt-1">
                  Realm {status.realm_id} · Connected {new Date(status.connected_at).toLocaleDateString()}
                  {status.last_sync_at && ` · Last sync ${new Date(status.last_sync_at).toLocaleString()}`}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="p-5 flex flex-wrap gap-2">
        {status.enabled && !status.connected && (
          <Button onClick={connect} disabled={busy}
            className="bg-green-600 hover:bg-green-700 text-white gap-2" data-testid="qbo-connect-btn">
            Connect QuickBooks
          </Button>
        )}
        {status.connected && (
          <>
            <Button onClick={sync} disabled={busy}
              className="bg-green-600 hover:bg-green-700 text-white gap-2" data-testid="qbo-sync-btn">
              Sync now
            </Button>
            <Button variant="outline" onClick={disconnect} disabled={busy}
              className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="qbo-disconnect-btn">
              Disconnect
            </Button>
          </>
        )}
        {!status.enabled && (
          <span className="text-xs text-slate-500 self-center">
            Admin: set QBO_CLIENT_ID, QBO_CLIENT_SECRET, and QBO_REDIRECT_URI in backend .env to enable.
          </span>
        )}
      </div>
    </div>
  );
};

export const Connectors = ({ token }) => {
  const [connectors, setConnectors] = useState([]);
  const [sourceTypes, setSourceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [newConnectorName, setNewConnectorName] = useState('');
  const [regenerating, setRegenerating] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [connRes, stRes] = await Promise.all([
        axios.get(`${API}/connectors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/connectors/source-types`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setConnectors(connRes.data.connectors || []);
      setSourceTypes(stRes.data.source_types || []);
    } catch (err) {
      toast.error('Failed to load connectors');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreated = (data) => {
    setNewApiKey(data.api_key);
    setNewConnectorName(data.connector?.name || '');
    setShowForm(false);
    toast.success('Connector created');
    fetchData();
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/connectors/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setConnectors(prev => prev.filter(c => c.id !== id));
      toast.success('Connector deleted');
    } catch { toast.error('Failed to delete connector'); }
    finally { setDeleting(null); }
  };

  const handleRegenerate = async (id) => {
    setRegenerating(id);
    try {
      const res = await axios.post(`${API}/connectors/${id}/regenerate-key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewApiKey(res.data.api_key);
      const c = connectors.find(c => c.id === id);
      setNewConnectorName(c?.name || '');
      toast.success('API key regenerated');
      fetchData();
    } catch { toast.error('Failed to regenerate key'); }
    finally { setRegenerating(null); }
  };

  const handleToggleStatus = async (connector) => {
    const newStatus = connector.status === 'active' ? 'disabled' : 'active';
    try {
      await axios.put(`${API}/connectors/${connector.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectors(prev => prev.map(c => c.id === connector.id ? { ...c, status: newStatus } : c));
      toast.success(`Connector ${newStatus}`);
    } catch { toast.error('Failed to update connector'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 size={32} className="animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="connectors-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">Connectors</h1>
          <p className="text-slate-600">Configure API connectors for automated data ingestion from ERPs, P-Cards, and other systems</p>
        </div>
        {!showForm && (
          <Button onClick={() => { setShowForm(true); setNewApiKey(null); }}
            className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1.5" data-testid="add-connector-btn">
            <Plus size={16} /> New Connector
          </Button>
        )}
      </div>

      {newApiKey && (
        <ApiKeyReveal apiKey={newApiKey} connectorName={newConnectorName} />
      )}

      <QuickBooksCard token={token} />

      {showForm && (
        <ConnectorForm sourceTypes={sourceTypes} token={token}
          onCreated={handleCreated} onCancel={() => setShowForm(false)} />
      )}

      {connectors.length === 0 && !showForm ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center max-w-md">
            <Plug size={48} className="text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-700">No connectors configured</p>
            <p className="text-sm text-slate-500 mt-1 mb-6">
              Connectors let external systems push spend data directly into DataRefinery via secure API endpoints. Manual upload remains available for all users.
            </p>
            <Button onClick={() => setShowForm(true)} className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1.5" data-testid="add-first-connector-btn">
              <Plus size={16} /> Create Your First Connector
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="connectors-list">
          {connectors.map(connector => {
            const IconComp = ICON_MAP[sourceTypes.find(s => s.id === connector.source_type)?.icon] || Globe;
            return (
              <div key={connector.id} data-testid={`connector-row-${connector.id}`}
                className={`bg-white border rounded-sm p-5 transition-all ${connector.status === 'active' ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-slate-100 rounded-sm">
                    <IconComp size={18} className="text-slate-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-slate-900">{connector.name}</span>
                      <SourceTypeTag sourceType={connector.source_type} sourceTypes={sourceTypes} />
                      <StatusBadge status={connector.status} />
                    </div>

                    <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                      <span className="font-mono">{connector.api_key_masked}</span>
                      <span className="flex items-center gap-1">
                        <Zap size={10} />
                        {connector.total_ingestions} ingestion{connector.total_ingestions !== 1 ? 's' : ''}
                      </span>
                      <span>{connector.total_records_ingested.toLocaleString()} records</span>
                      {connector.industry && (
                        <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px] font-medium">{connector.industry}</span>
                      )}
                      {connector.auto_process && (
                        <span className="flex items-center gap-0.5 text-emerald-600 font-medium">
                          <Zap size={10} /> Auto
                        </span>
                      )}
                    </div>

                    {connector.last_ingestion && (
                      <p className="text-[10px] text-slate-400 mt-1">
                        Last ingestion: {new Date(connector.last_ingestion).toLocaleString()}
                      </p>
                    )}

                    <IngestionHistory connectorId={connector.id} token={token} />
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => handleToggleStatus(connector)}
                      className={`p-1.5 rounded-sm transition-colors text-xs font-medium px-2 ${connector.status === 'active' ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'}`}
                      data-testid={`connector-toggle-${connector.id}`}>
                      {connector.status === 'active' ? 'Active' : 'Disabled'}
                    </button>
                    <button onClick={() => handleRegenerate(connector.id)}
                      disabled={regenerating === connector.id}
                      className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-sm transition-colors"
                      data-testid={`connector-regen-${connector.id}`}
                      title="Regenerate API key">
                      {regenerating === connector.id ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
                    </button>
                    <button onClick={() => handleDelete(connector.id)}
                      disabled={deleting === connector.id}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                      data-testid={`connector-delete-${connector.id}`}>
                      {deleting === connector.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3" data-testid="connectors-help">
        <p className="text-xs font-semibold text-slate-700">How Connectors Work</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <p className="text-xs font-medium text-slate-600">1. Create a Connector</p>
            <p className="text-[11px] text-slate-400">Choose a source type (ERP, P-Card, etc.), set your field mapping, and get a unique API key.</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-slate-600">2. Send Data via API</p>
            <p className="text-[11px] text-slate-400">POST a JSON array of records to the ingest endpoint with your API key in the X-API-Key header.</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-slate-600">3. Automatic Processing</p>
            <p className="text-[11px] text-slate-400">If enabled, ingested data is automatically cleansed and classified using AI with your industry context.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
